import React, { useState, useEffect } from 'react';
import { Mail, CheckCircle2, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import SEO from './SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UrlsForSale = () => {
  const [urls, setUrls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contactEmail, setContactEmail] = useState('info@shireadvisors.com');
  const [inquiryUrl, setInquiryUrl] = useState(null);
  const [inquiryForm, setInquiryForm] = useState({ name: '', email: '', phone: '', company: '', message: '' });
  const [submittingInquiry, setSubmittingInquiry] = useState(false);
  const [inquirySubmitted, setInquirySubmitted] = useState(false);

  const openInquiry = (url) => {
    setInquiryForm({
      name: '',
      email: '',
      phone: '',
      company: '',
      message: `I would like to make an offer on ${url.name}.\n\n`,
    });
    setInquirySubmitted(false);
    setInquiryUrl(url);
  };

  const closeInquiry = () => {
    setInquiryUrl(null);
  };

  const submitInquiry = async (e) => {
    e.preventDefault();
    if (!inquiryUrl) return;
    setSubmittingInquiry(true);
    const wrappedMessage = `[URL inquiry — ${inquiryUrl.name}]\n\n${inquiryForm.message.trim()}`;
    try {
      await axios.post(`${API}/contact/submit`, {
        ...inquiryForm,
        message: wrappedMessage,
      });
      setInquirySubmitted(true);
      toast.success("Inquiry sent — we'll be in touch soon.");
      setTimeout(() => closeInquiry(), 1800);
    } catch (err) {
      toast.error('Could not send inquiry. Please try again or call us directly.');
    } finally {
      setSubmittingInquiry(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [urlsRes, contactRes] = await Promise.all([
          axios.get(`${API}/content/urls-for-sale`),
          axios.get(`${API}/content/contact`),
        ]);
        const visibleUrls = (urlsRes.data || [])
          .filter((u) => u.visible !== false)
          .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
        setUrls(visibleUrls);
        if (contactRes.data?.email) {
          setContactEmail(contactRes.data.email);
        }
      } catch (error) {
        console.error('Error fetching URLs for sale', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Structured data: CollectionPage + ItemList of Products w/ Offers
  // Helps Google understand this is a marketplace catalog rather than a
  // generic content page. Each available item becomes its own indexable
  // Product line with a deep-link anchor.
  const SITE_URL = 'https://shireadvisors.com';
  const PAGE_URL = `${SITE_URL}/urlsforsale`;
  const visibleAvailable = urls.filter((u) => u.status !== 'sold');
  const urlsJsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'URLs for Sale', item: PAGE_URL },
        ],
      },
      {
        '@type': 'CollectionPage',
        '@id': PAGE_URL,
        url: PAGE_URL,
        name: 'Premium Domain Names for Sale | Shire Advisors',
        description:
          "Curated portfolio of brandable premium domain names for sale. First-fair-offer-wins. Hospitality, robotics, sustainability, energy, and Texas regional domains.",
        inLanguage: 'en-US',
        isPartOf: { '@type': 'WebSite', url: SITE_URL, name: 'Shire Advisors' },
        mainEntity: {
          '@type': 'ItemList',
          numberOfItems: visibleAvailable.length,
          itemListOrder: 'https://schema.org/ItemListOrderAscending',
          itemListElement: visibleAvailable.map((u, idx) => ({
            '@type': 'ListItem',
            position: idx + 1,
            item: {
              '@type': 'Product',
              '@id': `${PAGE_URL}#${(u.name || '').toLowerCase()}`,
              name: u.name,
              description: u.description || `Premium domain name ${u.name} for sale.`,
              brand: { '@type': 'Brand', name: 'Shire Advisors Domains' },
              category: 'Domain Name',
              offers: {
                '@type': 'Offer',
                url: `${PAGE_URL}#${(u.name || '').toLowerCase()}`,
                priceCurrency: 'USD',
                priceSpecification: {
                  '@type': 'PriceSpecification',
                  priceCurrency: 'USD',
                  description: 'First-fair-offer-wins — make an offer.',
                },
                availability: 'https://schema.org/InStock',
                seller: {
                  '@type': 'Organization',
                  name: 'Shire Advisors, LLC',
                  url: SITE_URL,
                  email: contactEmail,
                },
              },
            },
          })),
        },
      },
    ],
  };

  return (
    <section
      className="pt-32 pb-10 bg-gradient-to-br from-gray-50 via-white to-gold-50/30"
      data-testid="urls-for-sale-page"
    >
      <SEO
        title="Premium Domain Names for Sale"
        description="Curated portfolio of premium, brandable domain names for sale by Shire Advisors — hospitality, robotics, cleantech, energy, and Texas regional brands. First-fair-offer-wins basis. Inquire today."
        path="/urlsforsale"
        jsonLd={urlsJsonLd}
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-6">
          <span className="inline-block text-gold-600 font-semibold text-xs tracking-[0.2em] uppercase mb-2">
            Marketplace
          </span>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-bold text-navy-900 mb-2 leading-tight">
            URLs &amp; Web Addresses for Sale
          </h1>
          <p className="text-sm text-gray-600 max-w-2xl mx-auto leading-relaxed mb-3">
            The following URLs are available for sale. They&apos;re resold on a{' '}
            <span className="text-navy-900 font-semibold">
              first-fair-offer-wins
            </span>{' '}
            basis — just send us an email/offer.
          </p>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-navy-900 text-white rounded-full text-sm">
            <Mail className="w-4 h-4 text-gold-400" />
            <a
              href={`mailto:${contactEmail}?subject=URL%20Inquiry`}
              className="font-medium hover:text-gold-400 transition-colors"
              data-testid="urls-contact-email"
            >
              {contactEmail}
            </a>
          </div>
        </div>

        {/* URL list */}
        {loading ? (
          <div className="text-center text-gray-500 py-8 text-sm">Loading…</div>
        ) : urls.length === 0 ? (
          <div className="text-center text-gray-500 py-8 text-sm">
            No URLs are currently listed.
          </div>
        ) : (
          <div className="space-y-3">
            {urls.map((url) => {
              const isSold = url.status === 'sold';
              return (
                <article
                  key={url.id}
                  id={(url.name || '').toLowerCase()}
                  itemScope
                  itemType="https://schema.org/Product"
                  className={`group relative overflow-hidden rounded-xl border-2 transition-all duration-300 ${
                    isSold
                      ? 'bg-gray-50 border-slate-200/70 opacity-75'
                      : 'panel-flair bg-white border-slate-200/70 hover:border-gold-400 hover:-translate-y-0.5'
                  }`}
                  data-testid={`url-item-${url.id}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 px-5 py-4 sm:px-6">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <h3
                          itemProp="name"
                          className={`text-lg sm:text-xl font-serif font-bold tracking-tight ${
                            isSold
                              ? 'text-gray-400 line-through'
                              : 'text-navy-900 group-hover:text-navy-800'
                          }`}
                        >
                          {url.name}
                        </h3>
                        {isSold ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-[10px] font-bold uppercase tracking-wider">
                            <CheckCircle2 className="w-3 h-3" />
                            Sold
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 bg-gold-100 text-gold-700 rounded-full text-[10px] font-bold uppercase tracking-wider">
                            Available
                          </span>
                        )}
                      </div>
                      {url.description && (
                        <p
                          itemProp="description"
                          className={`text-sm leading-snug ${
                            isSold ? 'text-gray-400' : 'text-gray-600'
                          }`}
                        >
                          {url.description}
                        </p>
                      )}
                    </div>
                    {!isSold && (
                      <div className="flex-shrink-0">
                        <Button
                          onClick={() => openInquiry(url)}
                          size="sm"
                          className="bg-navy-900 hover:bg-navy-800 text-white"
                          data-testid={`url-inquire-${url.id}`}
                        >
                          <Mail className="w-3.5 h-3.5 mr-1.5" />
                          Make an Offer
                        </Button>
                      </div>
                    )}
                  </div>
                  {/* gold accent strip on available items */}
                  {!isSold && (
                    <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-gradient-to-b from-gold-400 to-gold-600" />
                  )}
                </article>
              );
            })}
          </div>
        )}

        {/* Bottom CTA */}
        <div className="mt-10 text-center text-xs text-gray-500 max-w-2xl mx-auto">
          All URLs are sold as-is. Transfer is handled via the registrar of the
          buyer's choosing. Contact us for offers, questions, or to discuss
          financing.
        </div>
      </div>

      {/* Inquiry / Offer Modal */}
      <Dialog
        open={!!inquiryUrl}
        onOpenChange={(open) => !open && closeInquiry()}
      >
        <DialogContent
          className="max-w-lg w-[95vw] p-0 overflow-hidden"
          data-testid="url-inquiry-modal"
        >
          <DialogHeader className="px-6 pt-5 pb-3 border-b bg-navy-900 text-white">
            <DialogTitle className="text-white font-serif text-xl pr-8">
              Make an Offer
            </DialogTitle>
            <DialogDescription className="text-gold-200 text-sm">
              {inquiryUrl?.name}
            </DialogDescription>
          </DialogHeader>

          {inquirySubmitted ? (
            <div className="p-8 text-center" data-testid="url-inquiry-success">
              <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-serif font-bold text-navy-900 mb-1">
                Inquiry Sent
              </h3>
              <p className="text-sm text-gray-600">
                Thanks — we&apos;ll get back to you shortly.
              </p>
            </div>
          ) : (
            <form onSubmit={submitInquiry} className="p-6 space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <Input
                  required
                  placeholder="Your name *"
                  value={inquiryForm.name}
                  onChange={(e) => setInquiryForm({ ...inquiryForm, name: e.target.value })}
                  data-testid="url-inquiry-name"
                />
                <Input
                  required
                  type="email"
                  placeholder="Email *"
                  value={inquiryForm.email}
                  onChange={(e) => setInquiryForm({ ...inquiryForm, email: e.target.value })}
                  data-testid="url-inquiry-email"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Input
                  type="tel"
                  placeholder="Phone (optional)"
                  value={inquiryForm.phone}
                  onChange={(e) => setInquiryForm({ ...inquiryForm, phone: e.target.value })}
                  data-testid="url-inquiry-phone"
                />
                <Input
                  placeholder="Company (optional)"
                  value={inquiryForm.company}
                  onChange={(e) => setInquiryForm({ ...inquiryForm, company: e.target.value })}
                  data-testid="url-inquiry-company"
                />
              </div>
              <Textarea
                required
                rows={5}
                placeholder="Share your offer or questions..."
                value={inquiryForm.message}
                onChange={(e) => setInquiryForm({ ...inquiryForm, message: e.target.value })}
                data-testid="url-inquiry-message"
              />
              <p className="text-[11px] text-gray-500 leading-relaxed">
                Submitting goes directly to {contactEmail}. All information is treated as confidential.
              </p>
              <div className="flex gap-2 pt-1">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={closeInquiry}
                  disabled={submittingInquiry}
                  data-testid="url-inquiry-cancel"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-navy-900 hover:bg-navy-800 text-white"
                  disabled={submittingInquiry}
                  data-testid="url-inquiry-submit"
                >
                  {submittingInquiry ? (
                    'Sending…'
                  ) : (
                    <>
                      <Send className="w-3.5 h-3.5 mr-1.5" />
                      Send Inquiry
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default UrlsForSale;
